import { Box, Grid } from "@mui/system";
import {
  Autocomplete,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Checkbox from "@mui/material/Checkbox";
import dayjs from "dayjs";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  clearOverviewConversionsFilterOptions,
  clearOverviewConversionsFilters,
  selectBizOverviewConversionsBaseFilterOptionsLoaded,
  selectBizOverviewConversionsDataSet,
  selectBizOverviewConversionsFilters,
  selectBizOverviewConversionsLoadedRequestKey,
  selectBizOverviewConversionsLoading,
  selectBizOverviewConversionsNotes,
  selectBizOverviewConversionsNotesLoadedRequestKey,
  selectBizOverviewConversionsNotesLoading,
  selectBizOverviewConversionsOutput,
  setOverviewConversionsDataSet,
  setOverviewConversionsOutput,
} from "../../../../../../features/bizDevelopmentDashboardSlice";
import { selectUserToken } from "../../../../../../features/userSlice";
import {
  buildBizOverviewConversionsNotesRequestKey,
  buildBizOverviewConversionsRequestKey,
  BizDevelopmentDashboardEndPointLogic,
  BizOverviewConversionsDataSet,
} from "../../../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardEndPointLogic";
import OverviewConversionsChart from "./OverviewConversionsChart";
import OverviewConversionsFilter from "./OverviewConversionsFilter";
import { exportOverviewConversionsToCSV } from "./overviewConversionsCsvHelpers";

export interface OverviewConversionsRef {
  refresh: () => void;
}

const OverviewConversions = forwardRef<OverviewConversionsRef>((_, ref) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const { userData } = useSelector((state: any) => state.user);
  const dataSet = useSelector(selectBizOverviewConversionsDataSet);
  const isLoading = useSelector(selectBizOverviewConversionsLoading);
  const isNotesLoading = useSelector(selectBizOverviewConversionsNotesLoading);
  const rows = useSelector(selectBizOverviewConversionsOutput);
  const notes = useSelector(selectBizOverviewConversionsNotes);
  const filters = useSelector(selectBizOverviewConversionsFilters);
  const loadedRequestKey = useSelector(
    selectBizOverviewConversionsLoadedRequestKey,
  );
  const notesLoadedRequestKey = useSelector(
    selectBizOverviewConversionsNotesLoadedRequestKey,
  );
  const baseFilterOptionsLoaded = useSelector(
    selectBizOverviewConversionsBaseFilterOptionsLoaded,
  );
  const [showNotes, setShowNotes] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);

  const selectedEndDate = useMemo(() => dayjs(), []);
  const selectedStartDate = useMemo(
    () => selectedEndDate.subtract(30, "day"),
    [selectedEndDate],
  );

  const selectedWebsiteUids = useMemo(() => {
    const sites = searchParams.get("sites");
    return sites ? sites.split(",").filter(Boolean) : [];
  }, [searchParams]);

  const defaultWebsiteUid = useMemo(
    () =>
      Object.keys(userData?.data?.website_permissions?.[0] || {})[0] || null,
    [userData?.data?.website_permissions],
  );
  const defaultDimension = userData?.data?.dimensions?.[0] || null;
  const websiteSharedUids =
    selectedWebsiteUids.length > 0
      ? selectedWebsiteUids
      : defaultWebsiteUid
        ? [defaultWebsiteUid]
        : [];
  const dimensions = defaultDimension ? [defaultDimension] : [];

  const availableLabels = useMemo(() => {
    if (!Array.isArray(notes)) return [];
    const labelSet = new Set<string>();
    notes.forEach((note: any) => {
      if (Array.isArray(note?.labels)) {
        note.labels.forEach((label: string) => labelSet.add(label));
      }
    });
    return Array.from(labelSet).sort();
  }, [notes]);

  const fetchConversions = (
    nextDataSet: BizOverviewConversionsDataSet = dataSet,
    nextFilters = filters,
    forceRefresh = false,
  ) => {
    if (!oauthTokenHeader || websiteSharedUids.length === 0) return;
    const requestKey = buildBizOverviewConversionsRequestKey({
      dataSet: nextDataSet,
      startDate: selectedStartDate.format("YYYY-MM-DD"),
      endDate: selectedEndDate.format("YYYY-MM-DD"),
      timeUnit: "day",
      websiteSharedUids,
      dimensions,
      filters: nextFilters,
    });
    if (!forceRefresh && loadedRequestKey === requestKey) return;
    BizDevelopmentDashboardEndPointLogic.getOverviewConversionsData({
      oauthTokenHeader,
      dataSet: nextDataSet,
      startDate: selectedStartDate.format("YYYY-MM-DD"),
      endDate: selectedEndDate.format("YYYY-MM-DD"),
      // startDate: "2025-06-06",
      // endDate: "2026-01-06",
      timeUnit: "day",
      websiteSharedUids,
      dimensions,
      filters: nextFilters,
    });
  };

  useEffect(() => {
    if (!oauthTokenHeader || websiteSharedUids.length === 0) return;
    fetchConversions(dataSet, filters);
    const notesRequestKey = buildBizOverviewConversionsNotesRequestKey({
      websiteSharedUids,
      fromDate: selectedStartDate.format("YYYY-MM-DD"),
      toDate: selectedEndDate.format("YYYY-MM-DD"),
    });
    if (notesLoadedRequestKey !== notesRequestKey) {
      BizDevelopmentDashboardEndPointLogic.getOverviewConversionsActivityNotes({
        oauthTokenHeader,
        websiteSharedUids,
        fromDate: selectedStartDate.format("YYYY-MM-DD"),
        toDate: selectedEndDate.format("YYYY-MM-DD"),
        // fromDate: "2025-06-06",
        // toDate: "2026-01-06",
      });
    }

    if (dataSet === "click" && !baseFilterOptionsLoaded) {
      BizDevelopmentDashboardEndPointLogic.getOverviewConversionsBaseFilterOptions(
        oauthTokenHeader,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthTokenHeader, selectedWebsiteUids.join(","), defaultWebsiteUid]);

  const handleDataSetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextDataSet = event.target.value as BizOverviewConversionsDataSet;
    dispatch(setOverviewConversionsDataSet(nextDataSet));
    dispatch(setOverviewConversionsOutput([]));
    dispatch(clearOverviewConversionsFilters());
    dispatch(clearOverviewConversionsFilterOptions());
    setSelectedLabels([]);
    const nextParams = new URLSearchParams(searchParams);
    ["trafficSource", "campaignId", "adgroupId", "kwId", "country"].forEach(
      (key) => nextParams.delete(key),
    );
    setSearchParams(nextParams);
    fetchConversions(nextDataSet, []);
  };

  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchConversions(dataSet, filters, true);
    },
  }));

  const handleDownload = () => {
    exportOverviewConversionsToCSV({
      rows: rows ?? [],
      filename: `overview_conversions_${dataSet}_date`,
    });
  };

  return (
    <>
      <Grid container spacing={2} alignItems="end" px={2} pb={1.5}>
        <Grid size={8}>
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <Typography>Last 30 Days CVR %</Typography>
            <Typography>by:</Typography>
            <FormControl disabled={isLoading}>
              <RadioGroup
                row
                aria-labelledby={""}
                name="row-radio-buttons-group"
                value={dataSet}
                onChange={handleDataSetChange}
              >
                <FormControlLabel
                  value="action"
                  control={<Radio color="secondary" />}
                  label="Action date"
                />
                <FormControlLabel
                  value="click"
                  control={<Radio color="secondary" />}
                  label="Click date"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Grid>
        <Grid size={4}>
          <Box display={"flex"} alignItems={"end"} justifyContent={"flex-end"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showNotes}
                  color="secondary"
                  disabled={isLoading || isNotesLoading || notes.length === 0}
                  onChange={(e) => {
                    setShowNotes(e.target.checked);
                    if (!e.target.checked) setSelectedLabels([]);
                  }}
                />
              }
              label="Show Notes"
            />
            <Autocomplete
              id="assetOrSiteName"
              multiple
              disablePortal
              disabled={isLoading || !showNotes}
              options={availableLabels.filter(
                (label) => !selectedLabels.includes(label),
              )}
              value={selectedLabels}
              onChange={(_, newValue) => setSelectedLabels(newValue)}
              getOptionLabel={(option) => option}
              size="small"
              sx={{ pt: "8px", width: "275px" }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="filled"
                    label={option}
                    size="small"
                    sx={{
                      bgcolor: "#7b1fa2",
                      color: "#fff",
                      "& .MuiChip-deleteIcon": {
                        color: "#e5d2ec",
                        "&:hover": {
                          color: "#d1c4e9",
                        },
                      },
                    }}
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  color="secondary"
                  {...params}
                  required
                  placeholder="Search Time Frame or choose from the list"
                  label="Filter Notes by Labels"
                />
              )}
            />
            <IconButton
              aria-label="download"
              color="secondary"
              sx={{ ml: 2 }}
              disabled={isLoading}
              onClick={handleDownload}
              data-tracker="836"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="end" px={2} pb={1.5}>
        <Grid size={12}>
          {dataSet === "click" && (
            <OverviewConversionsFilter
              oauthTokenHeader={oauthTokenHeader}
              dataSet={dataSet}
              websiteSharedUids={websiteSharedUids}
              dimensions={dimensions}
              onFiltersChange={(nextFilters) =>
                fetchConversions(dataSet, nextFilters)
              }
            />
          )}
        </Grid>
      </Grid>
      <OverviewConversionsChart
        loadingGraph={isLoading}
        showNotes={showNotes}
        selectedLabels={selectedLabels}
      />
    </>
  );
});

export default OverviewConversions;
